import type { SamplerPreset } from "../../../../src/lib/audio";
import {
  cloneSamplerPreset,
  createDefaultSamplerPreset,
  normalizeSamplerPreset,
} from "../../../../src/lib/audio";

/** Sampler bank shelves — parallel to wavetable / synth categories. */
export const SAMPLER_PRESET_CATEGORIES = [
  { id: "basic", label: "Basic" },
  { id: "pads", label: "Pads" },
  { id: "bass", label: "Bass" },
  { id: "leads", label: "Leads" },
  { id: "keys", label: "Keys" },
  { id: "drums", label: "Drums / chops" },
  { id: "experimental", label: "Experimental" },
  { id: "user", label: "User" },
] as const;

export type SamplerPresetCategoryId = typeof SAMPLER_PRESET_CATEGORIES[number]["id"];

export type NamedSamplerPreset = {
  id: string;
  name: string;
  builtin?: boolean;
  category: SamplerPresetCategoryId;
  tags?: string[];
  /** Stores sample ids + settings — PCM lives in the host sample library. */
  preset: SamplerPreset;
};

export function createDefaultSamplerBank(): NamedSamplerPreset[] {
  const preset = createDefaultSamplerPreset("Init Sine");
  return [
    {
      id: "builtin-sampler-init-sine",
      name: preset.name,
      builtin: true,
      category: "basic",
      tags: ["factory", "sine", "one-cycle"],
      preset: cloneSamplerPreset(preset),
    },
  ];
}

export function isSamplerPresetCategoryId(value: unknown): value is SamplerPresetCategoryId {
  return typeof value === "string" && SAMPLER_PRESET_CATEGORIES.some(c => c.id === value);
}

export function samplerCategoryLabel(id: SamplerPresetCategoryId): string {
  return SAMPLER_PRESET_CATEGORIES.find(c => c.id === id)?.label ?? id;
}

export function samplerCategorySortIndex(id: SamplerPresetCategoryId): number {
  const idx = SAMPLER_PRESET_CATEGORIES.findIndex(c => c.id === id);
  return idx >= 0 ? idx : SAMPLER_PRESET_CATEGORIES.length;
}

export function newSamplerPresetId(): string {
  return `user-smpl-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

export function sanitizeSamplerPresetCategory(
  value: unknown,
  fallback: SamplerPresetCategoryId = "user",
): SamplerPresetCategoryId {
  return isSamplerPresetCategoryId(value) ? value : fallback;
}

export function sanitizeSamplerPresetTags(raw: unknown): string[] | undefined {
  if (!Array.isArray(raw)) {
    return undefined;
  }
  const tags = raw
    .filter((t): t is string => typeof t === "string")
    .map(t => t.trim().toLowerCase())
    .filter(Boolean)
    .slice(0, 12);
  return tags.length > 0 ? [...new Set(tags)] : undefined;
}

/** Lightweight shape check for import JSON (normalization fills the rest). */
export function isSamplerPresetShape(value: unknown): value is SamplerPreset {
  if (!value || typeof value !== "object") {
    return false;
  }
  const preset = value as SamplerPreset;
  return Boolean(preset.filter && preset.ampEnv && Array.isArray(preset.zones));
}

export function sanitizeSamplerBank(raw: unknown): NamedSamplerPreset[] {
  const factory = createDefaultSamplerBank();
  if (!Array.isArray(raw)) {
    return factory;
  }
  const byId = new Map<string, NamedSamplerPreset>();
  for (const item of raw) {
    if (!item || typeof item !== "object") {
      continue;
    }
    const entry = item as Partial<NamedSamplerPreset>;
    if (typeof entry.id !== "string" || !entry.id) {
      continue;
    }
    const preset = normalizeSamplerPreset(entry.preset);
    byId.set(entry.id, {
      id: entry.id,
      name: typeof entry.name === "string" && entry.name.trim() ? entry.name.trim() : preset.name,
      builtin: !!entry.builtin,
      category: isSamplerPresetCategoryId(entry.category) ? entry.category : "user",
      ...(sanitizeSamplerPresetTags(entry.tags)
        ? { tags: sanitizeSamplerPresetTags(entry.tags) }
        : {}),
      preset,
    });
  }
  const out: NamedSamplerPreset[] = [];
  for (const factoryEntry of factory) {
    const user = byId.get(factoryEntry.id);
    if (user) {
      out.push({
        ...factoryEntry,
        name: user.name || factoryEntry.name,
        category: user.category,
        tags: user.tags ?? factoryEntry.tags,
        preset: user.preset,
        builtin: true,
      });
      byId.delete(factoryEntry.id);
    } else {
      out.push(factoryEntry);
    }
  }
  for (const user of byId.values()) {
    if (!user.builtin) {
      out.push({ ...user, builtin: false, category: user.category || "user" });
    }
  }
  return out;
}

/**
 * Import / open project: take the file's bank as base, then keep any non-builtin
 * presets already in the session that the file doesn't include (by id).
 * Sample PCM still lives in the host library — these are just named patch refs.
 */
export function mergeSamplerBanksPreservingUser(
  imported: readonly NamedSamplerPreset[] | undefined,
  existing: readonly NamedSamplerPreset[] | undefined,
): NamedSamplerPreset[] {
  const base = sanitizeSamplerBank(imported ?? createDefaultSamplerBank());
  if (!existing?.length) {
    return base;
  }
  const ids = new Set(base.map(entry => entry.id));
  const out = [...base];
  for (const entry of existing) {
    if (entry.builtin || ids.has(entry.id)) {
      continue;
    }
    out.push({
      ...entry,
      builtin: false,
      category: entry.category || "user",
      preset: cloneSamplerPreset(entry.preset),
      ...(entry.tags ? { tags: [...entry.tags] } : {}),
    });
    ids.add(entry.id);
  }
  return out;
}
